import { useEffect, useMemo, useRef, useState, CSSProperties, useCallback } from "react";
import { AssignedLabelItem } from "../types";
import SelectDropdown from "@/components/common/dropdown/SelectDropdown";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";
import { cn } from "@/lib/utils";
import { SqButton } from "@/components/squircle-ui/button";
import { getLabelColors } from "@/screens/utils/Helpers";

import {
  RiArrowRightLine,
  RiAddLine,
  RiStackLine,
  RiCloseLine,
  RiDraggable,
} from "@remixicon/react";

export interface LabelManagementProps {
  uniqueLabels: string[];
  currentLabelItems?: AssignedLabelItem[];
  onLabelSelect?: (label: AssignedLabelItem) => void;
  onLabelsSave?: (labels: AssignedLabelItem[]) => void;
  labelView?: string;
  creativeKind?: "image" | "video" | "mixed";
  customTrigger?: React.ReactNode; // New prop for custom trigger
  handleMenuOpenChange?: (open: boolean) => void;
}

const LabelManagement = ({
  uniqueLabels,
  currentLabelItems,
  onLabelSelect,
  onLabelsSave,
  labelView,
  creativeKind,
  customTrigger,
  handleMenuOpenChange,
}: LabelManagementProps) => {
  const isVideoSequence = creativeKind === "video" && !!labelView;
  const isMultiple = creativeKind === "image" && labelView === "multiple";
  const allOptions = useMemo(
    () => uniqueLabels.map((label) => ({ key: label, label, value: label })),
    [uniqueLabels],
  );
  const options = useMemo(() => {
    if (isMultiple || isVideoSequence) return allOptions;
    const currentLabel = currentLabelItems?.[0]?.label ?? "";
    return allOptions.filter((opt) => opt.value !== currentLabel);
  }, [allOptions, isMultiple, isVideoSequence, currentLabelItems]);

  const selectedSingle = currentLabelItems?.[0]?.label ?? "";
  const selectedMultiple = useMemo(
    () => (isMultiple ? (currentLabelItems ?? []).map((l) => l.label) : []),
    [currentLabelItems, isMultiple],
  );

  const sortedItems = useMemo(() => {
    const list = [...(currentLabelItems ?? [])];
    return list.sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER),
    );
  }, [currentLabelItems]);

  // State for video sequence popover and draft items; defined unconditionally to respect Hooks rules
  const [sequenceOpen, setSequenceOpen] = useState(false);
  const [draftItems, setDraftItems] = useState<AssignedLabelItem[]>([]);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [isListScrollable, setIsListScrollable] = useState(false);
  // Auto-scroll while dragging
  const dragPointerYRef = useRef<number | null>(null);
  const autoScrollRafRef = useRef<number | null>(null);
  const autoScrollActiveRef = useRef<boolean>(false);
  const { handleDragStart, handleDragOver, handleDrop, handleDragEnd, isDragOver } =
    useDragAndDrop<AssignedLabelItem>({
      items: draftItems,
      setItems: (items) =>
        setDraftItems(items.map((it, idx) => ({ label: it.label, order: idx + 1 }))),
      getItemId: (item) => `${item.order ?? 0}-${item.label}`,
      getGhostElement: (item) =>
        itemRefs.current[`${item.order ?? 0}-${item.label}`] || null,
    });

  const checkListScrollable = () => {
    if (listContainerRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = listContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 20;
      setIsListScrollable(scrollHeight > clientHeight && !isAtBottom);
    }
  };

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRafRef.current != null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
    autoScrollActiveRef.current = false;
  }, []);

  const autoScrollStep = useCallback(() => {
    const container = listContainerRef.current;
    const pointerY = dragPointerYRef.current;
    if (!container || pointerY == null) {
      stopAutoScroll();
      return;
    }
    const rect = container.getBoundingClientRect();
    const threshold = 32; // px from edge to start autoscroll
    let direction = 0;
    let distance = 0;
    if (pointerY < rect.top + threshold) {
      direction = -1;
      distance = rect.top + threshold - pointerY;
    } else if (pointerY > rect.bottom - threshold) {
      direction = 1;
      distance = pointerY - (rect.bottom - threshold);
    }
    if (direction === 0) {
      stopAutoScroll();
      return;
    }
    const maxSpeed = 14; // px/frame
    const minSpeed = 2;
    const clamped = Math.min(threshold, Math.max(0, distance));
    const speed = minSpeed + (clamped / threshold) * (maxSpeed - minSpeed);
    const prev = container.scrollTop;
    container.scrollTop += direction * speed;
    // If we cannot scroll further, stop
    if (container.scrollTop === prev) {
      stopAutoScroll();
      return;
    }
    autoScrollRafRef.current = requestAnimationFrame(autoScrollStep);
  }, [stopAutoScroll]);

  const maybeStartAutoScroll = useCallback(() => {
    if (autoScrollActiveRef.current) return;
    autoScrollActiveRef.current = true;
    autoScrollRafRef.current = requestAnimationFrame(autoScrollStep);
  }, [autoScrollStep]);

  const updateAutoScrollFromEvent = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      dragPointerYRef.current = e.clientY;
      const container = listContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const threshold = 32;
      const y = e.clientY;
      const nearTop = y < rect.top + threshold;
      const nearBottom = y > rect.bottom - threshold;
      const canScroll = container.scrollHeight > container.clientHeight;
      if (canScroll && (nearTop || nearBottom)) {
        maybeStartAutoScroll();
      } else if (autoScrollActiveRef.current) {
        stopAutoScroll();
      }
    },
    [maybeStartAutoScroll, stopAutoScroll],
  );

  useEffect(() => {
    if (!sequenceOpen) {
      stopAutoScroll();
    }
    return () => stopAutoScroll();
  }, [sequenceOpen, stopAutoScroll]);

  const handleSingleChange = (value: string) => {
    const existing = (currentLabelItems ?? []).find((l) => l.label === value);
    const item: AssignedLabelItem = existing ?? { label: value, order: null };
    onLabelSelect?.(item);
    handleMenuOpenChange?.(false);
  };
  if (!isVideoSequence) {
    return (
      <SelectDropdown
        options={options}
        selected={selectedSingle}
        onChange={(value) => handleSingleChange(value)}
        dropdownAlign="start"
        enableSearch={true}
        multiSelect={isMultiple}
        multiSelectShowSaveButton={isMultiple}
        multiSelectSaveLabel="Save"
        selectedValues={isMultiple ? selectedMultiple : undefined}
        onChangeMultiple={
          isMultiple
            ? (values) => {
                const items: AssignedLabelItem[] = values.map((v) => {
                  const existing = (currentLabelItems ?? []).find((l) => l.label === v);
                  return existing ?? { label: v, order: null };
                });
                onLabelsSave?.(items);
                handleMenuOpenChange?.(false);
              }
            : undefined
        }
        showOutline={false}
        sortSelectedToTop={true}
        sortAlphabetically={true}
        triggerClassName={
          customTrigger
            ? "flex shrink-0"
            : "flex opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 pointer-events-none group-hover:pointer-events-auto data-[state=open]:pointer-events-auto transition-opacity shrink-0 justify-center items-center w-[26px] h-[26px] rounded-[6px] border-[1px] border-border-gray shadow-sm hover:bg-gray-50"
        }
        customTrigger={
          customTrigger || (
            <Button
              variant={"sq-outline"}
              className="border-none squircle-radius-[6px] squircle-outline-color-[#E3E6EB]"
              onClick={(e) => e.stopPropagation()}
            >
              <RiArrowRightLine size={14} className="text-brand-primary-400 shrink-0" />
            </Button>
          )
        }
      />
    );
  }

  // Video sequence mode: show popover with current ordered sequence and a plus trigger to append
  const handleAppend = (value: string) => {
    const base = draftItems.length > 0 ? draftItems : sortedItems;
    const nextList = [...base, { label: value, order: (base.length || 0) + 1 }];
    setDraftItems(nextList.map((it, idx) => ({ label: it.label, order: idx + 1 })));
    // After appending, scroll to bottom to reveal the new item
    setTimeout(() => {
      const el = listContainerRef.current;
      if (el) {
        if (typeof el.scrollTo === "function") {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        } else {
          el.scrollTop = el.scrollHeight;
        }
        // Update fade state after scroll
        checkListScrollable();
      }
    }, 0);
  };
  const handleRemove = (index: number) => {
    const base = draftItems.length > 0 ? draftItems : sortedItems;
    const next = base.filter((_, i) => i !== index);
    setDraftItems(next.map((it, idx) => ({ label: it.label, order: idx + 1 })));
  };
  const displayedItems = draftItems.length > 0 ? draftItems : sortedItems;
  const hasChanges = JSON.stringify(displayedItems) !== JSON.stringify(sortedItems);
  return (
    <Popover
      open={sequenceOpen}
      modal
      onOpenChange={(open) => {
        setSequenceOpen(open);
        if (open) {
          setDraftItems(sortedItems);
        } else {
          setDraftItems([]);
        }
      }}
    >
      <PopoverTrigger asChild>
        {customTrigger || (
          <Button
            variant={"sq-outline"}
            className="border-none squircle-radius-[6px] squircle-outline-color-[#E3E6EB] opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 pointer-events-none group-hover:pointer-events-auto data-[state=open]:pointer-events-auto transition-opacity w-[26px] h-[26px] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <RiStackLine
              size={14}
              className="text-brand-primary-400 shrink-0 -rotate-90"
            />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          "w-[250px] p-[6px] shadow-none border-none squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white",
        )}
        sideOffset={10}
        alignOffset={10}
      >
        <div className="flex flex-col items-start gap-2">
          <div className="relative w-full">
            <div
              ref={listContainerRef}
              onScroll={checkListScrollable}
              className="w-full flex flex-col gap-[6px] max-h-[300px] overflow-y-auto scrollbar-hide"
            >
              {displayedItems.map((item, idx) => {
                const idKey = `${item.order ?? 0}-${item.label}`;
                const candidateIndex = uniqueLabels.findIndex((l) => l === item.label);
                const safeIndex = candidateIndex >= 0 ? candidateIndex : idx;
                const colors = getLabelColors(safeIndex);
                return (
                  <div
                    key={`${item.label}-${idx}-${item.order ?? idx}`}
                    ref={(el) => (itemRefs.current[idKey] = el)}
                    draggable
                    onDragStart={(e) => handleDragStart(item, e)}
                    onDragOver={(e) => {
                      handleDragOver(item, e);
                      updateAutoScrollFromEvent(e);
                    }}
                    onDrop={(e) => {
                      handleDrop(item, e);
                      stopAutoScroll();
                    }}
                    onDragEnd={() => {
                      stopAutoScroll();
                      handleDragEnd();
                    }}
                    className={cn(
                      "group w-full overflow-hidden max-w-[250px] flex items-center justify-between gap-2 h-[28px] shrink-0 px-2 rounded-[6px] squircle squircle-smooth-[1] squircle-radius-[6px] squircle-outline-[1px]",
                      isDragOver(item) && "border border-dashed border-blue-400",
                    )}
                    style={
                      {
                        backgroundColor: colors.background,
                        "--squircle-fill": colors.background,
                      } as CSSProperties
                    }
                  >
                    <div className="flex-1 overflow-hidden flex items-center gap-[6px]">
                      <RiDraggable
                        className="group-hover:w-[14px] w-0 h-[14px] shrink-0 overflow-hidden transition-all duration-300 cursor-grab"
                        size={14}
                        style={{ color: colors.icon }}
                      />

                      <span
                        className="text-[11px] h-[14px] truncate"
                        style={{ color: colors.text }}
                      >
                        {idx + 1}
                        {". "}
                        {item.label}
                      </span>
                    </div>
                    <button
                      className="group-hover:w-[18px] shrink-0 w-0 overflow-hidden transition-all duration-300 h-[18px] flex items-center justify-center rounded-[4px] hover:bg-hover-gray"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(idx);
                      }}
                      aria-label="Remove"
                    >
                      <RiCloseLine size={14} style={{ color: colors.icon }} />
                    </button>
                  </div>
                );
              })}
            </div>
            {isListScrollable && (
              <div
                className="absolute bottom-0 left-0 right-0 h-[59px] pointer-events-none"
                style={{
                  background:
                    "linear-gradient(0deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)",
                }}
              />
            )}
          </div>
          <div className="w-full flex items-center justify-between gap-1">
            <SelectDropdown
              options={allOptions}
              selected={""}
              onChange={(value) => handleAppend(value)}
              dropdownAlign="start"
              enableSearch={true}
              showOutline={false}
              sortAlphabetically={true}
              triggerClassName="h-[26px] w-[26px] flex items-center justify-center cursor-pointer"
              customTrigger={
                <div onClick={(e) => e.stopPropagation()}>
                  <RiAddLine size={14} className="text-brand-primary-400 shrink-0" />
                </div>
              }
            />
            <SqButton
              variant={"outline"}
              size={"sm"}
              disabled={!hasChanges}
              className="text-brand-primary-600 squircle-radius-[6px]"
              onClick={(e) => {
                e.stopPropagation();
                onLabelsSave?.(displayedItems);
                setSequenceOpen(false);
                handleMenuOpenChange?.(false);
              }}
            >
              Save
            </SqButton>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LabelManagement;
